/**
 * SmartInspect Consultant Workspace — ROI Results tab.
 * Recomputes the deterministic ROI from the LATEST saved assessment inputs
 * (via the shared engine) whenever the tab is opened, and embeds the exact
 * same SmartInspectResults UI used by the public calculator — minus the
 * website chrome and customer action buttons (embedded mode).
 *
 * Also hosts the "Finalize Executive Report" action (Sprint 8M): enabled only
 * when a valid ROI has been calculated (not blocked by capacity / custom
 * pricing), disabled with a hint otherwise.
 */
import { useEffect, useState } from "react";
import { Loader2, CheckCircle2, FileText } from "lucide-react";
import { toast } from "sonner";
import { siCalculateAssessment, siFinalizeReport } from "../lib/smartInspectApi";
import { SmartInspectResults } from "./SmartInspectResults";
import { Button } from "../components/ui/button";

export default function SmartInspectRoiResultsTab({ id, assessment, readOnly = false, onFinalized, onGotoReport }) {
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [finalizing, setFinalizing] = useState(false);

  useEffect(() => {
    let active = true;
    setLoading(true);
    setErr("");
    siCalculateAssessment(id)
      .then((r) => { if (active) setResult(r?.roi_result || null); })
      .catch(() => { if (active) setErr("Could not calculate ROI from the current inputs."); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [id]);

  const _annual = result?.annual_results || {};
  const canFinalize = !!result && result.calculation_status !== "calculation_failed" && !result.roi_blocked
    && (_annual.inspection_volume || 0) > 0 && (_annual.total_direct_savings || 0) > 0;
  const isFinalized = assessment?.status === "finalized" || assessment?.is_finalized;

  const handleFinalize = async () => {
    if (!canFinalize || finalizing) return;
    setFinalizing(true);
    const toastId = toast.loading("Finalizing Executive Report…");
    try {
      const res = await siFinalizeReport(id);
      toast.success(`Executive Report finalized (v${res?.version?.version_number || 1}).`, { id: toastId });
      onFinalized?.(res?.assessment);
      onGotoReport?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not finalize the Executive Report.", { id: toastId });
    } finally {
      setFinalizing(false);
    }
  };

  if (loading) {
    return (
      <div className="p-12 text-center text-sm text-[#6B7280]" data-testid="si-roi-loading">
        <Loader2 className="w-5 h-5 animate-spin inline mr-2" /> Calculating latest ROI…
      </div>
    );
  }
  if (err || !result || result.calculation_status === "calculation_failed") {
    return (
      <div className="rounded-xl border border-dashed border-[#E5E7EB] bg-[#FAFAFA] p-12 text-center" data-testid="si-roi-error">
        <p className="text-sm text-[#6B7280]">{err || "ROI results are not available yet. Complete the Client Inputs to generate the business case."}</p>
      </div>
    );
  }

  return (
    <div data-testid="si-roi-embedded" className="space-y-4">
      {/* Finalize band (Sprint 8M) */}
      {!readOnly && (
        <div className="rounded-xl border border-[#E5E7EB] bg-white p-4 flex flex-wrap items-center justify-between gap-3" data-testid="si-finalize-band">
          <div className="flex items-start gap-3">
            <div className="mt-0.5 w-9 h-9 rounded-lg bg-[#F0F9FF] flex items-center justify-center">
              <FileText className="w-4.5 h-4.5 text-[#025979]" />
            </div>
            <div>
              <div className="text-sm font-semibold text-[#111827]">Executive Report</div>
              {isFinalized ? (
                <p className="text-xs text-emerald-700 inline-flex items-center gap-1 mt-0.5" data-testid="si-finalize-status">
                  <CheckCircle2 className="w-3.5 h-3.5" /> Finalized as v{assessment?.report_version || assessment?.finalized_version_number || 1}. Re-finalize to capture the latest inputs.
                </p>
              ) : canFinalize ? (
                <p className="text-xs text-[#6B7280] mt-0.5">Freeze the assessment and ROI into a client-ready business case.</p>
              ) : (
                <p className="text-xs text-amber-700 mt-0.5" data-testid="si-finalize-disabled-hint">
                  A valid ROI must be successfully calculated before the Executive Report can be finalized.
                </p>
              )}
            </div>
          </div>
          <Button
            data-testid="si-finalize-btn"
            onClick={handleFinalize}
            disabled={!canFinalize || finalizing}
            className="bg-[#025979] hover:bg-[#03738C] text-white disabled:opacity-50"
          >
            {finalizing ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <FileText className="w-4 h-4 mr-1.5" />}
            {isFinalized ? "Re-finalize Executive Report" : "Finalize Executive Report"}
          </Button>
        </div>
      )}

      <SmartInspectResults result={result} embedded />
    </div>
  );
}
